import type { CityConfig } from "../city-template/types";

export const livermoreConfig: CityConfig = {
  city: "Livermore",
  state: "California",
  stateAbbr: "CA",
  slug: "livermore",
  route: "/livermore",
  title: "Emergency Passport Livermore, CA — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Livermore, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Tri-Valley and East Bay travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Livermore, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Livermore and East Bay travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Livermore%20California%20at%20golden%20hour%20with%20rolling%20golden%20hills%20and%20vineyard%20landscape%2C%20downtown%20historic%20district%20with%20charming%20storefronts%2C%20Livermore%20Valley%20wine%20country%20with%20rows%20of%20grapevines%2C%20warm%20sunset%20light%20over%20the%20Tri-Valley%20area%2C%20professional%20landscape%20photography%2C%20serene%20East%20Bay%20suburban%20environment%2C%20no%20visible%20text%20or%20people%2C%20warm%20amber%20and%20green%20tones&width=1800&height=700&seq=livermore-hero-001&orientation=landscape",
  heroImageAlt: "Livermore California at golden hour with rolling hills, vineyards, and historic downtown",
  geoPosition: "37.6819;-121.7680",
  geoPlacename: "Livermore, California",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "95 Hawthorne St Suite 200, San Francisco, CA 94105",
    distance: "Located in San Francisco — approximately 45–60 minutes from Livermore via I-580",
  },
  nearbyCitiesZips: [
    { name: "Livermore", zips: "94550, 94551" },
    { name: "Pleasanton", zips: "94566, 94588" },
    { name: "Dublin", zips: "94568, 94582" },
    { name: "San Ramon", zips: "94582, 94583" },
    { name: "Danville", zips: "94506, 94526" },
    { name: "Tracy", zips: "95304, 95376, 95377, 95378, 95391" },
    { name: "Brentwood", zips: "94513" },
    { name: "Antioch", zips: "94509, 94531" },
    { name: "Walnut Creek", zips: "94595, 94596, 94597, 94598" },
    { name: "Concord", zips: "94518, 94519, 94520, 94521, 94522, 94523, 94524, 94527, 94529" },
    { name: "Hayward", zips: "94540, 94541, 94542, 94543, 94544, 94545, 94546, 94552, 94557" },
    { name: "Fremont", zips: "94536, 94537, 94538, 94539, 94555" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Tri-Valley departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "San Francisco", route: "/san-francisco" },
    { name: "San Jose", route: "/san-jose" },
    { name: "Oakland", route: "/oakland" },
    { name: "Berkeley", route: "/berkeley" },
    { name: "Concord", route: "/concord" },
    { name: "Hayward", route: "/hayward" },
    { name: "Stockton", route: "/stockton" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Livermore remotely via secure courier. We do not have a walk-in office in Livermore — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Tri-Valley travelers.",
  fpcOfficeAddress: "400 S El Camino Real, San Mateo, CA 94402",
  fpcOfficeDistance: "Serving all of Alameda County and the Tri-Valley",
  serviceAreaIntro: "From the downtown historic district to the vineyards, from the labs to the suburbs — Fast Passport Center serves the entire Livermore community. We regularly help travelers from Pleasanton, Dublin, San Ramon, and Danville get their passports fast.",
  nearbyCitiesIntro: "We serve all Livermore neighborhoods and nearby Tri-Valley and East Bay cities including Pleasanton, Dublin, San Ramon, Danville, Tracy, and Brentwood. If you are in Alameda County or the East Bay, we have you covered.",
  howItWorksIntro: "Most Livermore travelers complete everything online and ship documents overnight. No need to drive to San Francisco or wait in line at the regional agency.",
  mistakesIntro: "With SFO and OAK serving as the Bay Area's primary international airports, Livermore travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Livermore and Tri-Valley travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Livermore? Here are the most common ones we hear from Tri-Valley travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Tri-Valley international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Chris D.",
      location: "Livermore, CA",
      rating: 5,
      text: "My business trip to Tokyo was in 3 days and my passport was expired. Fast Passport Center got me a new one in 36 hours. Saved the deal completely.",
      service: "Emergency Passport",
    },
    {
      name: "Maria S.",
      location: "Pleasanton, CA",
      rating: 5,
      text: "Needed to renew for a family trip to Mexico. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Kevin T.",
      location: "Dublin, CA",
      rating: 5,
      text: "Lost my passport a week before my honeymoon to Italy. They walked me through the DS-64 process and got me a replacement in 48 hours. Incredible.",
      service: "Lost Passport",
    },
    {
      name: "Linda R.",
      location: "San Ramon, CA",
      rating: 5,
      text: "As a frequent international traveler for work, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "lv-1",
      name: "Livermore Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "100 S Livermore Ave",
      city: "Livermore",
      state: "CA",
      zip: "94550",
      phone: "(925) 447-0139",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Livermore",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "lv-2",
      name: "Pleasanton Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "4800 Hopyard Rd",
      city: "Pleasanton",
      state: "CA",
      zip: "94588",
      phone: "(925) 461-4166",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "2.5 miles from downtown Pleasanton",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "lv-3",
      name: "Dublin Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "6937 Village Pkwy",
      city: "Dublin",
      state: "CA",
      zip: "94568",
      phone: "(925) 829-9054",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Dublin",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular location for Tri-Valley residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "lv-4",
      name: "San Ramon Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2000 Crow Canyon Pl",
      city: "San Ramon",
      state: "CA",
      zip: "94583",
      phone: "(925) 277-2040",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.8 miles from Bishop Ranch",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Contra Costa County residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "lv-5",
      name: "Tracy Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "835 W 11th St",
      city: "Tracy",
      state: "CA",
      zip: "95376",
      phone: "(209) 832-2104",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Tracy",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for San Joaquin County residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "lv-6",
      name: "Alameda County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "1106 Madison St",
      city: "Oakland",
      state: "CA",
      zip: "94607",
      phone: "(510) 272-6362",
      hours: "Mon–Fri: 8:30 AM – 4:30 PM",
      distance: "30 miles from Livermore",
      website: "https://www.acgov.org/",
      websiteLabel: "Alameda County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — San Francisco", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Tri-Valley photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at East Bay acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Livermore applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the San Francisco regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Tri-Valley families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Livermore travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Livermore." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Livermore address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Livermore before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Livermore for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most Tri-Valley travelers." },
    { q: "How fast can I renew my passport in Livermore?", a: "Fast Passport Center offers fast passport renewal in Livermore in as little as 24–48 hours through our emergency courier service." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Livermore in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Livermore?", a: "Same-day passport appointments are only available at the SF Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Livermore residents." },
    { q: "Can I expedite a child passport in Livermore?", a: "Yes. Fast Passport Center expedites child passports in Livermore for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Livermore with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Livermore?",
      a: "With Fast Passport Center's emergency courier service, Livermore travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to visit the San Francisco Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Livermore?",
      a: "Popular options include the Livermore Post Office (94550), Pleasanton Post Office (94588), Dublin Post Office (94568), and San Ramon Post Office (94583). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Livermore?",
      a: "Same-day passports are only available at the San Francisco Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in the Tri-Valley or San Joaquin County?",
      a: "We serve all of the Tri-Valley including Pleasanton, Dublin, San Ramon, Danville, and Tracy. Our overnight shipping labels work from any UPS location, so your location within the East Bay doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Livermore?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Livermore?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Livermore and Pleasanton offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Livermore?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Livermore?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Tri-Valley families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Livermore", address: "4555 First St, Livermore, CA 94551", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Pleasanton", address: "3500 Bernal Ave, Pleasanton, CA 94566", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Dublin", address: "4950 Dublin Blvd, Dublin, CA 94568", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — San Ramon", address: "21001 San Ramon Valley Blvd, San Ramon, CA 94583", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Tracy", address: "2460 Naglee Rd, Tracy, CA 95304", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Danville", address: "450 Diablo Rd, Danville, CA 94526", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Brentwood", address: "2501 Sand Creek Rd, Brentwood, CA 94513", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Danville", address: "3150 Fostoria Way, Danville, CA 94526", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Livermore",
      address: "1717 N Vasco Rd, Livermore, CA 94551",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Pleasanton",
      address: "6300 W Las Positas Blvd, Pleasanton, CA 94588",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — San Ramon",
      address: "5300 San Ramon Valley Blvd, San Ramon, CA 94583",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Dublin",
      address: "6320 Dougherty Rd, Dublin, CA 94568",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Express services for Tri-Valley residents. Appointment required.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "35 miles from Livermore",
      address: "San Francisco, CA 94128",
      note: "The Bay Area's primary international airport. Accessible via I-580 W to I-80 W to US-101 N.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Flight Info",
      featured: true,
    },
    {
      code: "OAK",
      name: "Oakland International Airport",
      distance: "25 miles from Livermore",
      address: "1 Airport Dr, Oakland, CA 94621",
      note: "The closest international airport to Livermore. International flights to Mexico and domestic connections.",
      flightUrl: "https://www.oaklandairport.com/",
      flightLabel: "Oakland Airport",
      featured: false,
    },
    {
      code: "SJC",
      name: "San Jose International Airport",
      distance: "30 miles from Livermore",
      address: "1701 Airport Blvd, San Jose, CA 95110",
      note: "International flights to Mexico, Canada, and Europe. Accessible via I-680 S.",
      flightUrl: "https://www.flysanjose.com/",
      flightLabel: "San Jose Airport",
      featured: false,
    },
  ],
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    {
      label: "California Dept. of Public Health (Vital Records)",
      description: "State-issued birth and death certificates for all California counties",
      url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx",
    },
    {
      label: "Alameda County Clerk-Recorder (Birth, Death, Marriage Records)",
      description: "Birth certificates, death certificates, and marriage records for Alameda County",
      url: "https://www.acgov.org/auditor/clerk/",
    },
    {
      label: "Alameda County Superior Court (Divorce Records)",
      description: "Divorce decrees and court records for Alameda County",
      url: "https://www.alameda.courts.ca.gov/",
    },
  ],
};